/**
 * CAPEX - Board-Ready Executive Dashboard
 * Redesigned for executive steering and management decisions
 * Design: Quiet Luxury Corporativo - Angolan Night theme
 */

import React from 'react';
import {
  Target, CheckCircle, AlertTriangle, Calendar, ArrowUpRight, ArrowDownRight, Minus,
  TrendingUp, TrendingDown, Zap, Pause, RefreshCw, Factory, Wallet, Clock,
  ChevronRight, Shield, Gauge, Wrench, Layers, CircleDot, BarChart3, FileText, Package, Info, CreditCard
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, LabelList
} from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import ExecutiveDrilldownCard from '../../components/ExecutiveDrilldownCard';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getShortBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// ============ SEMANTIC COLOR SYSTEM - SCORECARD PATTERN ============
const statusColors = {
  positive: '#0C8A4B',
  positiveBg: '#EAF7EF',
  warning: '#A07A18',
  warningBg: '#F8F1DD',
  negative: '#C33D52',
  negativeBg: '#FBECEF',
};

const getSemanticBadgeStyle = (val, inverse = false, useWarning = true) => {
  if (val === undefined || val === null) return { bg: 'bg-slate-100', text: 'text-slate-600' };
  const isFavorable = inverse ? val <= 0 : val >= 0;
  const absVal = Math.abs(val);
  if (useWarning && !isFavorable && absVal <= 5) {
    return { bg: 'bg-amber-50', text: 'text-amber-700', bgColor: statusColors.warningBg, color: statusColors.warning };
  }
  if (isFavorable) {
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', bgColor: statusColors.positiveBg, color: statusColors.positive };
  }
  return { bg: 'bg-rose-100', text: 'text-rose-700', bgColor: statusColors.negativeBg, color: statusColors.negative };
};

const getTrendIconInfo = (vsBudget, vsYoY, inverse = false) => {
  const mainVal = vsBudget !== undefined ? vsBudget : vsYoY;
  if (mainVal === undefined) return null;
  const isFavorable = inverse ? mainVal <= 0 : mainVal >= 0;
  const isUp = mainVal > 0;
  return {
    isUp,
    bgColor: isFavorable ? statusColors.positiveBg : statusColors.negativeBg,
    iconColor: isFavorable ? statusColors.positive : statusColors.negative,
  };
};

// Executive Metric Card - SCORECARD EXECUTIVO PATTERN
const MetricCard = ({ 
  title, value, unit, vsBudget, vsYoY, changeUnit = '%', 
  icon: Icon, color = 'primary', subtitle, inverse = false, useWarning = true
}) => {
  // Get semantic styles
  const budgetStyle = getSemanticBadgeStyle(vsBudget, inverse, useWarning);
  const yoyStyle = getSemanticBadgeStyle(vsYoY, inverse, useWarning);
  const trendInfo = getTrendIconInfo(vsBudget, vsYoY, inverse);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-gold-300/50 transition-all duration-300">
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Title, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Title - Normal case, not uppercase */}
          <p className="text-xs text-slate-500 tracking-wide mb-1.5">{title}</p>
          
          {/* Value + Unit - Smaller size to match Personnel Costs */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-2xl font-semibold tracking-[-0.3px] text-primary">
              {value}
            </span>
            <span className="text-sm text-slate-400">{unit}</span>
          </div>
          
          {subtitle && <p className="text-xs text-slate-400 mb-2">{subtitle}</p>}
          
          {/* Comparison Badges - Inline compact style */}
          <div className="flex items-center gap-2 flex-wrap">
            {vsBudget !== undefined && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${budgetStyle.bg} ${budgetStyle.text}`}>
                {vsBudget >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {vsBudget > 0 ? '+' : ''}{vsBudget}{changeUnit} vs Orç.
              </span>
            )}
            {vsYoY !== undefined && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold ${yoyStyle.bg} ${yoyStyle.text}`}>
                {vsYoY >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {vsYoY > 0 ? '+' : ''}{vsYoY}{changeUnit} YoY
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Circular Trend Icon - SCORECARD PATTERN */}
        {trendInfo && (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <ArrowUpRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            ) : (
              <ArrowDownRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CapexInvestment = ({ year, language, setActiveSection, flowMap }) => {
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const budgetLabelShort = getShortBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  const vsOrcLabel = year === 2026 ? 'vs Orç. 1T' : 'vs Orç.';
  const yoyLabel = year === 2026 ? `vs ${previousPeriod}` : 'YoY';
  
  // =====================
  // KEY INSIGHTS - Executive messaging
  // =====================
  const capexInsights = {
    pt: [
      { 
        title: 'EXECUCAO', 
        description: '2025 consolidou activos criticos, com CAPEX executado abaixo do orcamento mas concentrado em prioridades operacionais de alto impacto.',
        color: 'text-gold-400',
        icon: Target
      },
      { 
        title: 'FOCO', 
        description: 'Exploracao e Metalurgia absorveram 87% do CAPEX, confirmando prioridade em capacidade produtiva e remocao de constrangimentos operacionais.',
        color: 'text-emerald-400',
        icon: CheckCircle
      },
      { 
        title: 'IMPLICACAO DE GESTAO', 
        description: 'Parte material do portefolio transitou para 2026, exigindo re-priorizacao para proteger liquidez sem comprometer activos criticos.',
        color: 'text-blue-400',
        icon: AlertTriangle
      },
    ],
    en: [
      { 
        title: 'EXECUTION', 
        description: '2025 consolidated critical assets, with CAPEX executed below budget but concentrated on high-impact operational priorities.',
        color: 'text-gold-400',
        icon: Target
      },
      { 
        title: 'FOCUS', 
        description: 'Exploration and Metallurgy absorbed 87% of CAPEX, confirming priority on productive capacity and operational bottleneck removal.',
        color: 'text-emerald-400',
        icon: CheckCircle
      },
      { 
        title: 'MANAGEMENT IMPLICATION', 
        description: 'Material portion of portfolio transitioned to 2026, requiring re-prioritization to protect liquidity without compromising critical assets.',
        color: 'text-blue-400',
        icon: AlertTriangle
      },
    ],
  };

  // =====================
  // BRIDGE DATA - Reconciliation with values for labels
  // =====================
  const bridgeData = [
    { 
      name: language === 'pt' ? 'Orcamento 2025' : 'Budget 2025', 
      value: 45.0, 
      fill: '#64748B',
      type: 'start',
      label: '45.0M'
    },
    { 
      name: language === 'pt' ? 'Reprogramado' : 'Rescheduled', 
      value: 15.3, 
      fill: '#C5A059',
      type: 'negative',
      label: '-15.3M'
    },
    { 
      name: language === 'pt' ? 'Cancelado' : 'Cancelled', 
      value: 2.0, 
      fill: '#DC2626',
      type: 'negative',
      label: '-2.0M'
    },
    { 
      name: language === 'pt' ? 'Diferido' : 'Deferred', 
      value: 1.0, 
      fill: '#F59E0B',
      type: 'negative',
      label: '-1.0M'
    },
    { 
      name: language === 'pt' ? 'CAPEX Executado' : 'Executed CAPEX', 
      value: 26.7, 
      fill: '#0A192F',
      type: 'end',
      label: '26.7M'
    },
  ];

  // Executive insight for reconciliation
  const reconciliationInsight = {
    pt: 'A principal diferenca face ao plano decorreu de reprogramacao para 2026 (USD 15,3M), mais do que de cancelamento estrutural do portefolio. Apenas USD 2M foram definitivamente retirados do plano.',
    en: 'The main difference from plan resulted from rescheduling to 2026 (USD 15.3M), rather than structural portfolio cancellation. Only USD 2M were definitively removed from the plan.'
  };

  // =====================
  // TOP 5 CRITICAL PROJECTS TABLE
  // =====================
  const topProjects = [
    { 
      name: { pt: 'Renovacao Frota Exploracao', en: 'Mining Fleet Renewal' },
      capex2025: 8.5,
      pctTotal: 31.8,
      status: 'completed',
      impact: { pt: 'Aumento de 15% na capacidade de movimentacao', en: '15% increase in movement capacity' },
      criticality: 'high'
    },
    { 
      name: { pt: 'Upgrade Planta Metalurgica', en: 'Metallurgical Plant Upgrade' },
      capex2025: 5.2,
      pctTotal: 19.5,
      status: 'on-track',
      impact: { pt: 'Melhoria de 8% na taxa de recuperacao', en: '8% improvement in recovery rate' },
      criticality: 'high'
    },
    { 
      name: { pt: 'Camioes CAT 777G', en: 'CAT 777G Trucks' },
      capex2025: 4.8,
      pctTotal: 18.0,
      status: 'completed',
      impact: { pt: 'Reducao de 20% em custos de manutencao', en: '20% reduction in maintenance costs' },
      criticality: 'high'
    },
    { 
      name: { pt: 'Sistema Manutencao Preventiva', en: 'Preventive Maintenance System' },
      capex2025: 3.2,
      pctTotal: 12.0,
      status: 'on-track',
      impact: { pt: 'Aumento de disponibilidade para 92%', en: 'Availability increase to 92%' },
      criticality: 'medium'
    },
    { 
      name: { pt: 'Infraestrutura Base Operacional', en: 'Operational Base Infrastructure' },
      capex2025: 2.5,
      pctTotal: 9.4,
      status: 'delayed',
      impact: { pt: 'Suporte a expansao de capacidade', en: 'Support for capacity expansion' },
      criticality: 'medium'
    },
  ];

  // =====================
  // CAPEX BY STRATEGIC NATURE - Simplified to 3 groups
  // =====================
  const capexByNatureSimplified = [
    { 
      category: { pt: 'Activos Criticos / Sustentacao', en: 'Critical Assets / Sustaining' },
      description: { pt: 'Protege a base operacional e mantem capacidade produtiva', en: 'Protects operational base and maintains productive capacity' },
      value: 14.3,
      pct: 54,
      budget: 17.0,
      icon: Shield,
      color: '#0A192F',
      highlight: true
    },
    { 
      category: { pt: 'Produtividade / Eficiencia', en: 'Productivity / Efficiency' },
      description: { pt: 'Remove constrangimentos e aumenta eficiencia operacional', en: 'Removes bottlenecks and increases operational efficiency' },
      value: 8.2,
      pct: 31,
      budget: 12.0,
      icon: Gauge,
      color: '#C5A059',
      highlight: true
    },
    { 
      category: { pt: 'Diferivel / Suporte', en: 'Deferrable / Support' },
      description: { pt: 'Pode ser adiado sem impacto critico imediato', en: 'Can be deferred without immediate critical impact' },
      value: 4.2,
      pct: 15,
      budget: 16.0,
      icon: Layers,
      color: '#94A3B8',
      highlight: false
    },
  ];

  const allocationInsight = {
    pt: 'A maior parte do CAPEX concentrou-se em activos criticos (54%) e produtividade (31%), preservando foco operacional e flexibilidade sobre investimentos menos essenciais.',
    en: 'Most CAPEX concentrated on critical assets (54%) and productivity (31%), preserving operational focus and flexibility over less essential investments.'
  };

  // =====================
  // RESCHEDULED PROJECTS - Enhanced with priority
  // =====================
  const rescheduledProjects = [
    { 
      name: { pt: 'Expansao Base Vida', en: 'Camp Expansion' }, 
      value: 5.2, 
      reason: { pt: 'Aguarda conclusao estudos de viabilidade', en: 'Awaiting feasibility studies completion' },
      milestone: { pt: 'T2 2026', en: 'Q2 2026' },
      risk: 'medium',
      priority: 'revalidate',
      impact: { pt: 'Capacidade de alojamento +40 pessoas', en: 'Housing capacity +40 people' },
      decision: { pt: 'Revalidar business case', en: 'Revalidate business case' },
      needsAttention: true
    },
    { 
      name: { pt: 'Upgrade Engenharia', en: 'Engineering Upgrade' }, 
      value: 2.8, 
      reason: { pt: 'Dependencia de equipamentos importados', en: 'Imported equipment dependency' },
      milestone: { pt: 'T2 2026', en: 'Q2 2026' },
      risk: 'medium',
      priority: 'revalidate',
      impact: { pt: 'Capacidade de resposta tecnica', en: 'Technical response capacity' },
      decision: { pt: 'Confirmar necessidade operacional', en: 'Confirm operational need' },
      needsAttention: true
    },
    { 
      name: { pt: 'Sistema IT Integrado', en: 'Integrated IT System' }, 
      value: 3.8, 
      reason: { pt: 'Reprogramacao de fornecedor principal', en: 'Main supplier rescheduling' },
      milestone: { pt: 'T1 2026', en: 'Q1 2026' },
      risk: 'low',
      priority: 'maintain',
      impact: { pt: 'Integracao operacional e reporting', en: 'Operational integration and reporting' },
      decision: { pt: 'Manter - baixo risco, alto valor', en: 'Maintain - low risk, high value' },
      needsAttention: false
    },
    { 
      name: { pt: 'Automacao Logistica', en: 'Logistics Automation' }, 
      value: 3.5, 
      reason: { pt: 'Priorizacao de investimento em frota', en: 'Fleet investment prioritization' },
      milestone: { pt: 'T3 2026', en: 'Q3 2026' },
      risk: 'low',
      priority: 'defer',
      impact: { pt: 'Reducao de 12% em custos logisticos', en: '12% reduction in logistics costs' },
      decision: { pt: 'Diferir - avaliar apos frota', en: 'Defer - evaluate after fleet' },
      needsAttention: false
    },
  ];

  const getPriorityBadge = (priority) => {
    const styles = {
      'revalidate': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', label: language === 'pt' ? 'Revalidar' : 'Revalidate' },
      'maintain': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', label: language === 'pt' ? 'Manter' : 'Maintain' },
      'defer': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', label: language === 'pt' ? 'Diferir' : 'Defer' }
    };
    const style = styles[priority] || styles['maintain'];
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${style.bg} ${style.text} ${style.border}`}>
        {style.label}
      </span>
    );
  };

  // =====================
  // MANAGEMENT DECISIONS - Enhanced
  // =====================
  const managementDecisions = {
    pt: {
      accelerate: {
        title: 'ACELERAR',
        description: 'Projectos com impacto directo em capacidade, fiabilidade ou producao que devem ser priorizados para proteger a execucao operacional de 2026.',
        items: ['Conclusao Upgrade Planta Metalurgica', 'Sistema Manutencao Preventiva'],
        implication: 'Proteger capacidade produtiva',
        color: 'emerald',
        icon: Zap
      },
      defer: {
        title: 'DIFERIR',
        description: 'Projectos de suporte ou discricionarios que podem ser adiados para preservar liquidez sem comprometer activos criticos ou continuidade operacional.',
        items: ['Automacao Logistica', 'Parte do upgrade de infraestrutura'],
        implication: 'Preservar liquidez',
        color: 'amber',
        icon: Pause
      },
      revalidate: {
        title: 'REVALIDAR',
        description: 'Projectos reprogramados cujo beneficio esperado ainda nao esta suficientemente comprovado ou cuja necessidade operacional deve ser revista.',
        items: ['Expansao Base Vida', 'Upgrade Engenharia'],
        implication: 'Confirmar business case',
        color: 'blue',
        icon: RefreshCw
      }
    },
    en: {
      accelerate: {
        title: 'ACCELERATE',
        description: 'Projects with direct impact on capacity, reliability or production that should be prioritized to protect 2026 operational execution.',
        items: ['Metallurgical Plant Upgrade completion', 'Preventive Maintenance System'],
        implication: 'Protect productive capacity',
        color: 'emerald',
        icon: Zap
      },
      defer: {
        title: 'DEFER',
        description: 'Support or discretionary projects that can be deferred to preserve liquidity without compromising critical assets or operational continuity.',
        items: ['Logistics Automation', 'Part of infrastructure upgrade'],
        implication: 'Preserve liquidity',
        color: 'amber',
        icon: Pause
      },
      revalidate: {
        title: 'REVALIDATE',
        description: 'Rescheduled projects whose expected benefit is not yet sufficiently proven or whose operational need should be reviewed.',
        items: ['Camp Expansion', 'Engineering Upgrade'],
        implication: 'Confirm business case',
        color: 'blue',
        icon: RefreshCw
      }
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'completed': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: language === 'pt' ? 'Concluido' : 'Completed' },
      'on-track': { bg: 'bg-blue-100', text: 'text-blue-700', label: language === 'pt' ? 'Em curso' : 'On Track' },
      'delayed': { bg: 'bg-amber-100', text: 'text-amber-700', label: language === 'pt' ? 'Atrasado' : 'Delayed' },
      'at-risk': { bg: 'bg-red-100', text: 'text-red-700', label: language === 'pt' ? 'Em risco' : 'At Risk' }
    };
    const style = styles[status] || styles['on-track'];
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  // Custom label component for bridge chart
  const renderCustomLabel = (props) => {
    const { x, y, width, value, index } = props;
    const item = bridgeData[index];
    return (
      <text 
        x={x + width / 2} 
        y={y - 8} 
        fill={item.type === 'end' ? '#0A192F' : '#64748B'} 
        textAnchor="middle" 
        fontSize={12}
        fontWeight={item.type === 'end' ? 700 : 600}
      >
        {item.label}
      </text>
    );
  };

  return (
    <div className="space-y-8" data-testid="capex-investment-section">
      {/* Page Title */}
      <div>
        <h1 
          className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
          style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
        >
          CAPEX | {titleSuffix}
        </h1>
        <p className="text-[15px] leading-[22px]" style={{ color: '#425070' }}>
          {comparisonSubtitle}
        </p>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* ===================== */}
      {/* MENSAGENS-CHAVE */}
      {/* ===================== */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {capexInsights[language].map((insight, idx) => {
            const IconComponent = insight.icon;
            return (
              <div 
                key={idx} 
                className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    idx === 0 ? 'bg-amber-500/30' : idx === 1 ? 'bg-emerald-500/30' : 'bg-blue-500/30'
                  }`}>
                    <IconComponent className={`w-5 h-5 ${
                      idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-emerald-400' : 'text-blue-400'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${insight.color}`}>{insight.title}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===================== */}
      {/* KPIs EXECUTIVOS */}
      {/* ===================== */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard 
          title={language === 'pt' ? 'CAPEX Executado 2025' : 'CAPEX Executed 2025'} 
          value="26.7" 
          unit="M USD" 
          icon={Wallet} 
          color="gold"
          vsBudget={-41}
          vsYoY={-8}
          changeUnit="%"
          inverse={true}
          subtitle={language === 'pt' ? '59% do orcamento aprovado' : '59% of approved budget'}
        />
        <MetricCard 
          title={language === 'pt' ? 'CAPEX Prioritario' : 'Priority CAPEX'} 
          value="87" 
          unit="%" 
          icon={Factory} 
          color="emerald"
          vsBudget={5}
          vsYoY={12}
          changeUnit="pp"
          subtitle={language === 'pt' ? 'Exploracao + Metalurgia' : 'Exploration + Metallurgy'}
        />
        <MetricCard 
          title={language === 'pt' ? 'Reprogramado p/ 2026' : 'Rescheduled to 2026'} 
          value="15.3" 
          unit="M USD" 
          icon={Clock}
          color="amber"
          vsBudget={-12}
          vsYoY={25}
          changeUnit="%"
          subtitle={language === 'pt' ? '34% do orcamento original' : '34% of original budget'}
        />
        <MetricCard 
          title={language === 'pt' ? 'Projectos Criticos em Atraso' : 'Critical Projects Delayed'} 
          value="2" 
          unit={language === 'pt' ? 'projectos' : 'projects'} 
          icon={AlertTriangle}
          color="primary"
          subtitle={language === 'pt' ? 'Requerem decisao de gestao' : 'Require management decision'}
        />
      </div>

      {/* ===================== */}
      {/* RESUMO EXECUTIVO - Layout Modular */}
      {/* ===================== */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Investimento e execução CAPEX' : 'Investment and CAPEX execution'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="capex-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Em 2025, o CAPEX executado totalizou USD 26,7M, equivalente a 59% do orçamento aprovado, ficando 41% abaixo do plano e 8% abaixo de 2024. A principal explicação foi a reprogramação de projectos para 2026, no montante de USD 15,3M. A execução abaixo do orçamento reflecte mais uma decisão de calendarização e priorização do que uma redução material da ambição de investimento.`
              : `In 2025, executed CAPEX totaled USD 26.7M, equivalent to 59% of approved budget, coming in 41% below plan and 8% below 2024. The main explanation was project rescheduling to 2026, amounting to USD 15.3M. Below-budget execution reflects more a scheduling and prioritization decision than a material reduction in investment ambition.`}
          </p>
        </div>

        {/* Driver Sections - Modular Cards */}
        <div className="space-y-4">
          {/* Execução e Desvios */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Execução e Desvios' : 'Execution and Variances'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Análise do CAPEX face ao orçamento' : 'CAPEX analysis vs budget'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O desvio face ao orçamento foi explicado sobretudo por reprogramação (USD 15,3M), não por cancelamento estrutural. Apenas USD 2,0M foram cancelados e USD 1,0M ficaram diferidos. O CAPEX prioritário fechou em 87%, acima do orçamento e de 2024.`
                : `Budget deviation was explained mainly by rescheduling (USD 15.3M), not structural cancellation. Only USD 2.0M were cancelled and USD 1.0M remained deferred. Priority CAPEX closed at 87%, above budget and 2024.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Métricas de Execução' : 'Execution Metrics'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'CAPEX Executado: USD 26,7M (59% do orçamento)' : 'Executed CAPEX: USD 26.7M (59% of budget)'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'Reprogramado para 2026: USD 15,3M' : 'Rescheduled to 2026: USD 15.3M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'CAPEX Prioritário: 87% (acima do plano)' : 'Priority CAPEX: 87% (above plan)'}
                </li>
              </ul>
            </div>
          </div>

          {/* Alocação de Capital */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Alocação de Capital' : 'Capital Allocation'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Distribuição por categoria de investimento' : 'Distribution by investment category'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A maior parte do CAPEX concentrou-se em Activos Críticos / Sustentação (USD 14,3M, 54% do total) e em Produtividade / Eficiência (USD 8,2M, 31% do total). Em conjunto, estas categorias representaram 85% do investimento executado.`
                : `Most CAPEX concentrated on Critical Assets / Sustaining (USD 14.3M, 54% of total) and Productivity / Efficiency (USD 8.2M, 31% of total). Together, these categories represented 85% of executed investment.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Top 5 Projectos' : 'Top 5 Projects'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Renovação Frota Exploração: USD 8,5M' : 'Mining Fleet Renewal: USD 8.5M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Upgrade Planta Metalúrgica: USD 5,2M' : 'Metallurgical Plant Upgrade: USD 5.2M'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Camiões CAT 777G: USD 4,8M' : 'CAT 777G Trucks: USD 4.8M'}
                </li>
              </ul>
            </div>
          </div>

          {/* Portfolio Reprogramado */}
          <div className="bg-gradient-to-r from-violet-50 to-slate-50 rounded-xl p-5 border border-violet-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Portfolio Reprogramado 2026' : '2026 Rescheduled Portfolio'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Investimentos diferidos para próximo ciclo' : 'Investments deferred to next cycle'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Os USD 15,3M adiados concentram-se em Expansão Base Vida, Sistema IT Integrado, Automação Logística e Upgrade Engenharia. A empresa optou por proteger liquidez e preservar activos mais críticos, deslocando investimentos de suporte para o ano seguinte.`
                : `The USD 15.3M deferred concentrates on Camp Expansion, Integrated IT System, Logistics Automation and Engineering Upgrade. The company chose to protect liquidity and preserve critical assets, shifting support investments to the following year.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-violet-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Projectos Reprogramados' : 'Rescheduled Projects'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  {language === 'pt' ? 'Expansão Base Vida' : 'Camp Expansion'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  {language === 'pt' ? 'Sistema IT Integrado' : 'Integrated IT System'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500"></div>
                  {language === 'pt' ? 'Automação Logística' : 'Logistics Automation'}
                </li>
              </ul>
            </div>
          </div>

          {/* Síntese à Gerência */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Síntese à Gerência' : 'Management Synthesis'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Pontos de atenção prioritários' : 'Priority attention points'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O CAPEX de 2025 foi mais baixo do que o planeado, mas mais concentrado nas prioridades certas. O capital executado protegeu a base produtiva e manteve o foco em activos com retorno operacional mais imediato. O tema seguinte será decidir o destino do portfolio reprogramado para 2026.`
                : `2025 CAPEX was lower than planned, but more concentrated on the right priorities. Executed capital protected the productive base and maintained focus on assets with more immediate operational return. The next theme will be deciding the fate of the portfolio rescheduled for 2026.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-amber-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Atenção Prioritária' : 'Priority Attention'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? '85% do CAPEX canalizado para activos críticos e produtividade' : '85% of CAPEX channeled to critical assets and productivity'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? '2 projectos críticos em atraso a avaliar' : '2 critical delayed projects to evaluate'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Decisão pendente sobre CAPEX reprogramado 2026' : 'Pending decision on 2026 rescheduled CAPEX'}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ============ DRILLDOWN NAVIGATION CARDS ============ */}
        {setActiveSection && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {language === 'pt' ? 'Análises Relacionadas' : 'Related Analysis'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <ExecutiveDrilldownCard
                icon={Wallet}
                title={language === 'pt' ? 'Liquidez' : 'Liquidity'}
                subtitle={language === 'pt' ? 'Impacto na caixa' : 'Cash impact'}
                navigateTo="liquidity"
                setActiveSection={setActiveSection}
                color="emerald"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Veja como o CAPEX impactou a geração de caixa livre.'
                    : 'See how CAPEX impacted free cash flow generation.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={CreditCard}
                title={language === 'pt' ? 'Dívida' : 'Debt'}
                subtitle={language === 'pt' ? 'Financiamento do plano' : 'Plan financing'}
                navigateTo="debt"
                setActiveSection={setActiveSection}
                color="rose"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise como a dívida financiou o programa de investimento.'
                    : 'Analyze how debt financed the investment program.'}
                </p>
              </ExecutiveDrilldownCard>
            </div>
          </div>
        )}
      </ExecutiveSummarySection>

      {/* ===================== */}
      {/* RECONCILIACAO CAPEX - Improved Bridge Chart */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="font-serif text-lg font-semibold text-primary mb-1">
              {language === 'pt' ? 'Reconciliacao do CAPEX: Orcamento vs Executado' : 'CAPEX Reconciliation: Budget vs Executed'}
            </h3>
            <p className="text-sm text-slate-500">
              {language === 'pt' 
                ? 'Porque ficou o CAPEX executado abaixo do plano aprovado?' 
                : 'Why was executed CAPEX below the approved plan?'}
            </p>
          </div>
        </div>

        {/* Executive Insight Box */}
        <div className="bg-slate-50 border-l-4 border-gold-500 rounded-r-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <CircleDot className="w-5 h-5 text-gold-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-700 leading-relaxed font-medium">
              {reconciliationInsight[language]}
            </p>
          </div>
        </div>
        
        <ResponsiveContainer width="100%" height={300}>
          <BarChart 
            data={bridgeData} 
            margin={{ top: 40, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 11, fill: '#64748B' }} 
              stroke="#94A3B8"
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: '#64748B' }} 
              stroke="#94A3B8"
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v}M`}
              domain={[0, 50]}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  const isNegative = data.type === 'negative';
                  const pctOfBudget = ((data.value / 45) * 100).toFixed(0);
                  return (
                    <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl">
                      <p className="text-sm font-medium text-gold-400 mb-1">{data.name}</p>
                      <p className="text-lg font-bold">
                        {isNegative ? '-' : ''}{data.value.toFixed(1)}M USD
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {pctOfBudget}% {language === 'pt' ? 'do orcamento' : 'of budget'}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {bridgeData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.fill}
                  stroke={entry.type === 'end' ? '#0A192F' : 'none'}
                  strokeWidth={entry.type === 'end' ? 3 : 0}
                />
              ))}
              <LabelList content={renderCustomLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <div className="flex justify-center gap-6 mt-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-slate-500 rounded"></div>
            <span>{language === 'pt' ? 'Orcamento' : 'Budget'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-gold-500 rounded"></div>
            <span>{language === 'pt' ? 'Reprogramado' : 'Rescheduled'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-red-600 rounded"></div>
            <span>{language === 'pt' ? 'Cancelado' : 'Cancelled'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-amber-500 rounded"></div>
            <span>{language === 'pt' ? 'Diferido' : 'Deferred'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 bg-primary rounded border-2 border-primary"></div>
            <span className="font-semibold">{language === 'pt' ? 'Executado' : 'Executed'}</span>
          </div>
        </div>
      </div>

      {/* ===================== */}
      {/* ALOCACAO ESTRATEGICA - Simplified 3 Groups */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Alocacao Estrategica do CAPEX' : 'Strategic CAPEX Allocation'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? 'Distribuicao por natureza de investimento e prioridade operacional' 
              : 'Distribution by investment nature and operational priority'}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6 mb-6">
          {capexByNatureSimplified.map((item, idx) => {
            const IconComponent = item.icon;
            const vsBudget = ((item.value - item.budget) / item.budget * 100).toFixed(0);
            return (
              <div 
                key={idx} 
                className={`rounded-xl p-5 border-2 transition-all ${
                  item.highlight 
                    ? 'bg-white border-slate-300 shadow-md' 
                    : 'bg-slate-50 border-slate-100'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <IconComponent className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{item.category[language]}</p>
                    <p className="text-xs text-slate-500">{item.description[language]}</p>
                  </div>
                </div>
                
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <p className="font-serif text-3xl font-bold text-primary">
                      {item.value.toFixed(1)}<span className="text-lg text-slate-400 ml-1">M</span>
                    </p>
                    <p className="text-sm text-slate-500">{item.pct}% {language === 'pt' ? 'do total' : 'of total'}</p>
                  </div>
                  <span className={`text-sm font-semibold px-2 py-1 rounded ${
                    parseInt(vsBudget) <= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {vsBudget}% Orc.
                  </span>
                </div>

                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all"
                    style={{ 
                      width: `${Math.min((item.value / item.budget) * 100, 100)}%`,
                      backgroundColor: item.color 
                    }}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1 text-right">
                  {language === 'pt' ? 'Orc.' : 'Budget'}: {item.budget.toFixed(1)}M
                </p>
              </div>
            );
          })}
        </div>

        {/* Allocation Insight */}
        <div className="bg-slate-50 border-l-4 border-emerald-500 rounded-r-lg p-4">
          <p className="text-sm text-slate-700 leading-relaxed">
            {allocationInsight[language]}
          </p>
        </div>
      </div>

      {/* ===================== */}
      {/* TOP 5 PROJECTOS CRITICOS */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Top 5 Projectos Criticos' : 'Top 5 Critical Projects'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? 'Projectos que movem materialmente o plano de investimento' 
              : 'Projects that materially move the investment plan'}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {language === 'pt' ? 'Projecto' : 'Project'}
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  CAPEX 2025
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  % {language === 'pt' ? 'do Total' : 'of Total'}
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {language === 'pt' ? 'Estado' : 'Status'}
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {language === 'pt' ? 'Impacto Esperado' : 'Expected Impact'}
                </th>
              </tr>
            </thead>
            <tbody>
              {topProjects.map((project, idx) => (
                <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-slate-800">{project.name[language]}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right font-semibold text-primary">{project.capex2025.toFixed(1)}M USD</td>
                  <td className="py-4 px-4 text-right text-slate-600">{project.pctTotal}%</td>
                  <td className="py-4 px-4 text-center">{getStatusBadge(project.status)}</td>
                  <td className="py-4 px-4 text-slate-600 max-w-xs">{project.impact[language]}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-semibold border-t-2 border-slate-200">
                <td className="py-3 px-4">{language === 'pt' ? 'Total Top 5' : 'Top 5 Total'}</td>
                <td className="py-3 px-4 text-right text-primary">24.2M USD</td>
                <td className="py-3 px-4 text-right">90.7%</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ===================== */}
      {/* PROJECTOS REPROGRAMADOS - Enhanced */}
      {/* ===================== */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="mb-6">
          <h3 className="font-serif text-lg font-semibold text-primary mb-1">
            {language === 'pt' ? 'Projectos Reprogramados para 2026' : 'Projects Rescheduled to 2026'}
          </h3>
          <p className="text-sm text-slate-500">
            {language === 'pt' 
              ? 'Ordenado por prioridade de decisao - projectos que requerem atencao imediata primeiro' 
              : 'Ordered by decision priority - projects requiring immediate attention first'}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {language === 'pt' ? 'Projecto' : 'Project'}
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {language === 'pt' ? 'Valor' : 'Value'}
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {language === 'pt' ? 'Prioridade' : 'Priority'}
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {language === 'pt' ? 'Risco' : 'Risk'}
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-blue-50">
                  {language === 'pt' ? 'Impacto Esperado' : 'Expected Impact'}
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider bg-gold-50">
                  {language === 'pt' ? 'Decisao Recomendada' : 'Recommended Decision'}
                </th>
              </tr>
            </thead>
            <tbody>
              {rescheduledProjects.map((project, idx) => (
                <tr 
                  key={idx} 
                  className={`border-b border-slate-100 transition-colors ${
                    project.needsAttention 
                      ? 'bg-amber-50/50 hover:bg-amber-50' 
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      {project.needsAttention && (
                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      )}
                      <span className="font-semibold text-slate-800">{project.name[language]}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">{project.reason[language]}</p>
                  </td>
                  <td className="py-4 px-4 text-right font-semibold text-primary text-sm whitespace-nowrap">{project.value}M USD</td>
                  <td className="py-4 px-4 text-center">{getPriorityBadge(project.priority)}</td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      project.risk === 'low' ? 'bg-emerald-100 text-emerald-700' :
                      project.risk === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {project.risk === 'low' ? (language === 'pt' ? 'Baixo' : 'Low') :
                       project.risk === 'medium' ? (language === 'pt' ? 'Medio' : 'Medium') :
                       (language === 'pt' ? 'Alto' : 'High')}
                    </span>
                  </td>
                  <td className="py-4 px-4 bg-blue-50/30">
                    <p className="text-sm font-medium text-slate-700">{project.impact[language]}</p>
                  </td>
                  <td className="py-4 px-4 bg-gold-50/30">
                    <span className={`inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-lg ${
                      project.priority === 'revalidate' 
                        ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                        : project.priority === 'defer'
                        ? 'bg-amber-100 text-amber-800 border border-amber-200'
                        : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    }`}>
                      <ChevronRight className="w-4 h-4" />
                      {project.decision[language]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-bold border-t-2 border-slate-300">
                <td className="py-4 px-4 text-base">{language === 'pt' ? 'Total Reprogramado' : 'Total Rescheduled'}</td>
                <td className="py-4 px-4 text-right text-primary text-lg">15.3M USD</td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ===================== */}
      {/* DECISOES REQUERIDAS - Enhanced */}
      {/* ===================== */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Decisoes Requeridas' : 'Required Decisions'}
          </h3>
        </div>

        <div className="grid grid-cols-3 gap-5">
          {Object.entries(managementDecisions[language]).map(([key, decision]) => {
            const IconComponent = decision.icon;
            const colorClasses = {
              emerald: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', icon: 'text-emerald-400', title: 'text-emerald-400', implication: 'bg-emerald-500/30 text-emerald-300' },
              amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/40', icon: 'text-amber-400', title: 'text-amber-400', implication: 'bg-amber-500/30 text-amber-300' },
              blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', icon: 'text-blue-400', title: 'text-blue-400', implication: 'bg-blue-500/30 text-blue-300' }
            };
            const colors = colorClasses[decision.color];
            
            return (
              <div 
                key={key}
                className={`${colors.bg} border ${colors.border} rounded-xl p-5 transition-all duration-300 hover:scale-[1.02]`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.bg}`}>
                    <IconComponent className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <h4 className={`font-bold text-base ${colors.title}`}>{decision.title}</h4>
                </div>
                <p className="text-slate-400 text-xs leading-relaxed mb-4">{decision.description}</p>
                
                <ul className="space-y-2 mb-4">
                  {decision.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <ChevronRight className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colors.icon}`} />
                      <span className="text-sm font-semibold text-white">{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Management Implication Tag */}
                <div className={`${colors.implication} rounded-lg px-3 py-2 text-center`}>
                  <p className="text-xs font-bold uppercase tracking-wide">{decision.implication}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CapexInvestment;
